import { useMemo, useState } from "react";

import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

import type { NavGroupId } from "@/widgets/app-sidebar/sidebarNav";
import { visibleItemsInGroup } from "@/widgets/app-sidebar/navVisibility";
import { useNavVisibilityCtx } from "@/widgets/app-sidebar/useNavVisibilityCtx";

import { useVerticalVocab } from "@/shared/lib/verticals";

import { foldCrumbs } from "./routes";
import { useBreadcrumbContext } from "./BreadcrumbProvider";

function CrumbSeparator() {
  return <span className="text-muted-foreground/60" aria-hidden>/</span>;
}

export function Breadcrumbs() {
  const { crumbs } = useBreadcrumbContext();
  const visibilityCtx = useNavVisibilityCtx();
  const v = useVerticalVocab();
  const [expanded, setExpanded] = useState(false);

  const displayCrumbs = useMemo(() => {
    if (crumbs.length === 0) return [];
    return crumbs;
  }, [crumbs]);

  const sectionMenuItems: MenuProps["items"] = useMemo(() => {
    const sectionCrumb = displayCrumbs.find((c) => c.isSection);
    const sectionGroupId = sectionCrumb?.sectionGroupId as NavGroupId | undefined;
    if (!sectionGroupId) return [];
    return visibleItemsInGroup(sectionGroupId, visibilityCtx).map((item) => ({
      key: item.to,
      label: (
        <Link to={item.to} className="text-foreground">
          {item.vocabKey ? v.nav[item.vocabKey] || item.label : item.label}
        </Link>
      ),
    }));
  }, [displayCrumbs, visibilityCtx, v.nav]);

  if (displayCrumbs.length === 0) return null;

  const fullLabel = displayCrumbs.map((c) => c.label).join(" / ");
  const { head, tail, folded } = expanded
    ? { head: displayCrumbs, tail: [], folded: [] }
    : foldCrumbs(displayCrumbs, 4);

  const sectionCrumb = displayCrumbs.find((c) => c.isSection);
  const sectionGroupId = sectionCrumb?.sectionGroupId as NavGroupId | undefined;

  const renderCrumb = (crumb: (typeof displayCrumbs)[0], index: number, isLast: boolean) => {
    if (crumb.isSection && sectionGroupId) {
      return (
        <Dropdown key={`section-${index}`} menu={{ items: sectionMenuItems }} trigger={["click"]}>
          <button
            type="button"
            className="inline-flex items-center gap-0.5 rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            {crumb.label}
            <ChevronDown className="size-3.5" aria-hidden />
          </button>
        </Dropdown>
      );
    }
    if (isLast || !crumb.href) {
      return (
        <span
          key={`${crumb.label}-${index}`}
          className="truncate text-sm font-medium text-foreground"
          aria-current={isLast ? "page" : undefined}
        >
          {crumb.label}
        </span>
      );
    }
    return (
      <Link
        key={`${crumb.href}-${index}`}
        to={crumb.href}
        className="truncate text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        {crumb.label}
      </Link>
    );
  };

  const visibleList = expanded || folded.length === 0
    ? displayCrumbs
    : [...head, ...tail];

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      {/* Desktop / tablet */}
      <ol
        className="hidden min-w-0 flex-wrap items-center gap-1.5 sm:flex"
        aria-label={fullLabel}
      >
        {!expanded && folded.length > 0 ? (
          <>
            {head.map((crumb, index) => (
              <li key={`h-${index}`} className="flex min-w-0 items-center gap-1.5">
                {renderCrumb(crumb, index, false)}
                <CrumbSeparator />
              </li>
            ))}
            <li className="flex items-center gap-1.5">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setExpanded(true)}
                aria-label="Show full breadcrumb trail"
              >
                …
              </button>
              <CrumbSeparator />
            </li>
            {tail.map((crumb, index) => {
              const isLast = index === tail.length - 1;
              return (
                <li key={`t-${index}`} className="flex min-w-0 items-center gap-1.5">
                  {renderCrumb(crumb, head.length + index + 1, isLast)}
                  {!isLast ? <CrumbSeparator /> : null}
                </li>
              );
            })}
          </>
        ) : (
          visibleList.map((crumb, index) => {
            const isLast = index === visibleList.length - 1;
            return (
              <li key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1.5">
                {renderCrumb(crumb, index, isLast)}
                {!isLast ? <CrumbSeparator /> : null}
              </li>
            );
          })
        )}
      </ol>

      {/* Mobile compact */}
      <div className="flex min-w-0 items-center gap-1.5 sm:hidden" aria-label={fullLabel}>
        {sectionCrumb ? (
          <>
            <Dropdown menu={{ items: sectionMenuItems }} trigger={["click"]}>
              <button
                type="button"
                className="shrink-0 text-xs font-medium text-muted-foreground"
              >
                {sectionCrumb.label}
              </button>
            </Dropdown>
            <CrumbSeparator />
          </>
        ) : null}
        <span className="truncate text-xs font-medium text-foreground" aria-current="page">
          {displayCrumbs[displayCrumbs.length - 1]?.label}
        </span>
      </div>
    </nav>
  );
}
